import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { vuser } from '../services/api';
import type {
  ChatViewerHumanLabelFields,
  ChatViewerLabelUpsertBody,
  ChatViewerResolvedLabel,
  ChatViewerStepVirtualReaction,
  Dialog,
  DialogAssistantStep,
  VirtualUser,
} from '../types/vuser';
import {
  getDialogStepBodyDisplay,
  getPipelineStepDisplayName,
  isDialogReasoningRole,
} from '../utils/vuserDialogDisplay';
import './ConnectorChatViewerPanel.css';
import { NOTIFICATION_AUTO_HIDE_MS } from '../constants/uiTiming';
import ChatMarkdownBody from './ui/ChatMarkdownBody';

/** Normalize vuser API envelopes: `{ data: T }` or raw `T`. */
function pickPayload<T>(resp: unknown): T | undefined {
  if (resp == null) return undefined;
  if (typeof resp === 'object' && 'data' in resp && (resp as { data?: T }).data !== undefined) {
    return (resp as { data: T }).data;
  }
  return resp as T;
}

function humanLabelSaveErrorMessage(e: unknown): string {
  if (e && typeof e === 'object' && 'response' in e) {
    const data = (e as { response?: { data?: unknown } }).response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object' && data !== null && 'message' in data) {
      const m = (data as { message?: unknown }).message;
      if (typeof m === 'string' && m.trim()) return m;
    }
  }
  if (e instanceof Error && e.message) return e.message;
  return 'Failed to save label';
}

function reactionLabel(v: unknown): string {
  if (v === 1) return 'Like';
  if (v === -1) return 'Dislike';
  if (v === 0) return 'Neutral';
  return '—';
}

/** Статусы диалога — `in_progress` / `completed`; всё прочее рисуем нейтрально,
 *  чтобы незнакомое значение не осталось плашкой без цвета. */
function dialogStatusClass(status: string | undefined): string {
  return status === 'completed' || status === 'in_progress' ? status : 'unknown';
}

function dialogStatusText(status: string | undefined): string {
  if (status === 'in_progress') return 'In progress';
  if (status === 'completed') return 'Completed';
  return status || 'unknown';
}

function SourceBadge({ source }: { source?: string }) {
  const s = (source || 'none').toLowerCase();
  const cls =
    s === 'human'
      ? 'chat-viewer-source-badge chat-viewer-source-badge--human'
      : s === 'admin'
        ? 'chat-viewer-source-badge chat-viewer-source-badge--admin'
        : s === 'user'
          ? 'chat-viewer-source-badge chat-viewer-source-badge--user'
          : 'chat-viewer-source-badge chat-viewer-source-badge--none';
  return <span className={cls}>{s}</span>;
}

function ResolvedBlock({ resolved }: { resolved: ChatViewerResolvedLabel | null | undefined }) {
  if (!resolved || typeof resolved !== 'object') {
    return <div className="chat-viewer-resolved">No resolved label yet.</div>;
  }
  const refText =
    (resolved.reference_answer as string | undefined) ??
    (resolved.ground_truth as string | undefined) ??
    '';
  return (
    <div className="chat-viewer-resolved">
      <div className="chat-viewer-resolved__row">
        <strong>Reaction:</strong> {reactionLabel(resolved.reaction)}
        <SourceBadge source={String(resolved.reaction_source || 'none')} />
      </div>
      {resolved.comment != null && String(resolved.comment).trim() !== '' && (
        <div className="chat-viewer-resolved__row">
          <strong>Comment:</strong> <SourceBadge source={String(resolved.comment_source || 'none')} />
          <div className="chat-viewer-resolved__md">
            <ChatMarkdownBody>{String(resolved.comment)}</ChatMarkdownBody>
          </div>
        </div>
      )}
      {refText.trim() !== '' && (
        <div className="chat-viewer-resolved__row">
          <strong>Reference:</strong>{' '}
          <SourceBadge source={String(resolved.ground_truth_source || 'none')} />
          <div className="chat-viewer-resolved__md">
            <ChatMarkdownBody>{refText}</ChatMarkdownBody>
          </div>
        </div>
      )}
    </div>
  );
}

type LabelDraft = {
  reaction: -1 | 0 | 1 | null;
  comment: string;
  reference_answer: string;
};

function emptyDraft(): LabelDraft {
  return { reaction: null, comment: '', reference_answer: '' };
}

function draftFromHuman(h: ChatViewerHumanLabelFields | null | undefined): LabelDraft {
  if (!h) return emptyDraft();
  const r = h.reaction;
  const refRaw = h.reference_answer ?? h.ground_truth;
  return {
    reaction: r === 1 || r === -1 || r === 0 ? r : null,
    comment: h.comment != null ? String(h.comment) : '',
    reference_answer: refRaw != null ? String(refRaw) : '',
  };
}

function HumanLabelEditor(props: {
  title: string;
  draft: LabelDraft;
  setDraft: React.Dispatch<React.SetStateAction<LabelDraft>>;
  saving: boolean;
  saveSuccess?: boolean;
  errorMessage?: string | null;
  onSave: () => void;
  onReload: () => void;
}) {
  const { title, draft, setDraft, saving, saveSuccess, errorMessage, onSave, onReload } = props;
  return (
    <div className="chat-viewer-human-form">
      <div className="chat-viewer-human-form__title">
        <i className="fas fa-user-pen" aria-hidden />
        {title}
      </div>
      <div className="chat-viewer-reaction-btns">
        {([-1, 0, 1] as const).map((rv) => (
          <button
            key={rv}
            type="button"
            aria-pressed={draft.reaction === rv}
            className={draft.reaction === rv ? 'chat-viewer-reaction--active' : ''}
            onClick={() => setDraft((d) => ({ ...d, reaction: d.reaction === rv ? null : rv }))}
          >
            {rv === 1 ? '👍 Like' : rv === -1 ? '👎 Dislike' : '○ Neutral'}
          </button>
        ))}
      </div>
      <textarea
        placeholder="Comment (optional)"
        value={draft.comment}
        onChange={(e) => setDraft((d) => ({ ...d, comment: e.target.value }))}
      />
      <textarea
        placeholder="Reference answer / ground truth (optional)"
        value={draft.reference_answer}
        onChange={(e) => setDraft((d) => ({ ...d, reference_answer: e.target.value }))}
      />
      {saveSuccess && (
        <div className="chat-viewer-save-toast" role="status" aria-live="polite">
          <i className="fas fa-check-circle" aria-hidden />
          <span>Saved successfully</span>
        </div>
      )}
      {errorMessage ? (
        <div className="chat-viewer-save-error" role="alert">
          <i className="fas fa-exclamation-circle" aria-hidden />
          <span>{errorMessage}</span>
        </div>
      ) : null}
      <div className="chat-viewer-human-form__actions">
        <button
          type="button"
          className={`chat-viewer-btn-primary ${saveSuccess && !saving ? 'chat-viewer-btn-primary--saved' : ''}`}
          disabled={saving}
          onClick={() => void onSave()}
        >
          {saving ? (
            <>
              <i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }} aria-hidden />
              Saving…
            </>
          ) : saveSuccess ? (
            <>
              <i className="fas fa-check" style={{ marginRight: 6 }} aria-hidden />
              Saved
            </>
          ) : (
            'Save human label'
          )}
        </button>
        <button type="button" className="chat-viewer-btn-secondary" disabled={saving} onClick={() => void onReload()}>
          Reload
        </button>
      </div>
    </div>
  );
}

export interface ConnectorChatViewerPanelProps {
  integrationId: string | null;
}

const ConnectorChatViewerPanel: React.FC<ConnectorChatViewerPanelProps> = ({ integrationId }) => {
  const [virtualUsers, setVirtualUsers] = useState<VirtualUser[]>([]);
  const [filterVuId, setFilterVuId] = useState<string>('');
  const [dialogs, setDialogs] = useState<Dialog[]>([]);
  const [dialogsLoading, setDialogsLoading] = useState(false);
  const [dialogsError, setDialogsError] = useState<string | null>(null);

  const [selectedDialogId, setSelectedDialogId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Dialog | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [messageResolved, setMessageResolved] = useState<Record<string, ChatViewerResolvedLabel | null>>({});
  const [expandedPipelineForMessage, setExpandedPipelineForMessage] = useState<string | null>(null);
  const [stepResolved, setStepResolved] = useState<Record<string, ChatViewerResolvedLabel | null>>({});
  const [stepReactions, setStepReactions] = useState<Record<string, ChatViewerStepVirtualReaction[]>>({});
  const [stepLoading, setStepLoading] = useState<Record<string, boolean>>({});

  const [messageDrafts, setMessageDrafts] = useState<Record<string, LabelDraft>>({});
  const [stepDrafts, setStepDrafts] = useState<Record<string, LabelDraft>>({});
  const [savingMessage, setSavingMessage] = useState<string | null>(null);
  const [savingStep, setSavingStep] = useState<string | null>(null);
  /** Keys `msg:${id}` / `step:${id}` — show success toast + button state until timeout */
  const [labelSaveSuccessKeys, setLabelSaveSuccessKeys] = useState<Record<string, true>>({});
  const [labelSaveErrorByKey, setLabelSaveErrorByKey] = useState<Record<string, string>>({});
  const labelSaveSuccessTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const flashLabelSaved = useCallback((key: string) => {
    if (labelSaveSuccessTimersRef.current[key]) {
      clearTimeout(labelSaveSuccessTimersRef.current[key]);
    }
    setLabelSaveSuccessKeys((prev) => ({ ...prev, [key]: true }));
    setLabelSaveErrorByKey((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    labelSaveSuccessTimersRef.current[key] = setTimeout(() => {
      setLabelSaveSuccessKeys((prev) => {
        if (!prev[key]) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
      delete labelSaveSuccessTimersRef.current[key];
    }, NOTIFICATION_AUTO_HIDE_MS);
  }, []);

  useEffect(() => {
    const timers = labelSaveSuccessTimersRef.current;
    return () => {
      Object.values(timers).forEach((t) => clearTimeout(t));
    };
  }, []);

  useEffect(() => {
    if (!integrationId) {
      setVirtualUsers([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const resp = await vuser.listVirtualUsers(integrationId);
        const list = resp.data || [];
        if (!cancelled && Array.isArray(list)) setVirtualUsers(list);
      } catch {
        if (!cancelled) setVirtualUsers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [integrationId]);

  const loadDialogs = useCallback(async () => {
    if (!integrationId) return;
    setDialogsLoading(true);
    setDialogsError(null);
    try {
      const resp = await vuser.listDialogs(
        integrationId,
        filterVuId.trim() ? filterVuId.trim() : undefined
      );
      const list = resp.data || [];
      setDialogs(Array.isArray(list) ? list : []);
      setSelectedDialogId(null);
      setDetail(null);
    } catch (e: unknown) {
      const msg = e && typeof e === 'object' && 'message' in e ? String((e as Error).message) : 'Failed to load dialogs';
      setDialogsError(msg);
      setDialogs([]);
    } finally {
      setDialogsLoading(false);
    }
  }, [integrationId, filterVuId]);

  useEffect(() => {
    void loadDialogs();
  }, [loadDialogs]);

  const loadDialogDetail = useCallback(
    async (dialogId: string) => {
      if (!integrationId) return;
      Object.values(labelSaveSuccessTimersRef.current).forEach((t) => clearTimeout(t));
      labelSaveSuccessTimersRef.current = {};
      setLabelSaveSuccessKeys({});
      setLabelSaveErrorByKey({});
      setDetailLoading(true);
      setDetailError(null);
      setMessageResolved({});
      setExpandedPipelineForMessage(null);
      setStepResolved({});
      setStepReactions({});
      setMessageDrafts({});
      setStepDrafts({});
      try {
        const resp = await vuser.getDialog(integrationId, dialogId);
        const d = resp.data;
        if (!d) throw new Error('Empty dialog response');
        setDetail(d);

        const messages = d.messages || [];
        const assistant = messages.filter((m) => m.role === 'assistant');
        const resolvedEntries = await Promise.all(
          assistant.map(async (m) => {
            try {
              const r = await vuser.chatViewerGetMessageResolvedLabel(integrationId, m.id);
              return [m.id, (pickPayload<ChatViewerResolvedLabel>(r) ?? null) as ChatViewerResolvedLabel | null] as const;
            } catch {
              return [m.id, null] as const;
            }
          })
        );
        setMessageResolved(Object.fromEntries(resolvedEntries));

        const draftEntries = await Promise.all(
          assistant.map(async (m) => {
            try {
              const r = await vuser.chatViewerGetMessageLabel(integrationId, m.id);
              const payload = pickPayload<{ label?: ChatViewerHumanLabelFields | null }>(r) ?? {};
              return [m.id, draftFromHuman(payload?.label)] as const;
            } catch {
              return [m.id, emptyDraft()] as const;
            }
          })
        );
        setMessageDrafts(Object.fromEntries(draftEntries));
      } catch (e: unknown) {
        const msg = e && typeof e === 'object' && 'message' in e ? String((e as Error).message) : 'Failed to load dialog';
        setDetailError(msg);
        setDetail(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [integrationId]
  );

  useEffect(() => {
    if (selectedDialogId && integrationId) {
      void loadDialogDetail(selectedDialogId);
    }
  }, [selectedDialogId, integrationId, loadDialogDetail]);

  const loadStepBundle = useCallback(
    async (stepId: string) => {
      if (!integrationId || !stepId) return;
      setStepLoading((s) => ({ ...s, [stepId]: true }));
      try {
        const [resR, resL, resV] = await Promise.all([
          vuser.chatViewerGetStepResolvedLabel(integrationId, stepId).catch(() => null),
          vuser.chatViewerGetStepLabel(integrationId, stepId).catch(() => null),
          vuser.chatViewerGetStepReactions(integrationId, stepId).catch(() => null),
        ]);
        if (resR) {
          setStepResolved((prev) => ({
            ...prev,
            [stepId]: pickPayload<ChatViewerResolvedLabel>(resR) ?? null,
          }));
        }
        if (resL) {
          const payload = pickPayload<{ label?: ChatViewerHumanLabelFields | null }>(resL) ?? {};
          setStepDrafts((prev) => ({
            ...prev,
            [stepId]: draftFromHuman(payload?.label),
          }));
        }
        if (resV) {
          const list = pickPayload<ChatViewerStepVirtualReaction[]>(resV);
          setStepReactions((prev) => ({
            ...prev,
            [stepId]: Array.isArray(list) ? list : [],
          }));
        }
      } finally {
        setStepLoading((s) => ({ ...s, [stepId]: false }));
      }
    },
    [integrationId]
  );

  const togglePipeline = useCallback(
    (messageId: string, steps: DialogAssistantStep[] | undefined) => {
      if (expandedPipelineForMessage === messageId) {
        setExpandedPipelineForMessage(null);
        return;
      }
      setExpandedPipelineForMessage(messageId);
      const ids = (steps || [])
        .map((s) => String((s as DialogAssistantStep).id || ''))
        .filter(Boolean);
      void Promise.all(ids.map((id) => loadStepBundle(id)));
    },
    [expandedPipelineForMessage, loadStepBundle]
  );

  const sortedMessages = useMemo(() => {
    const list = detail?.messages ? [...detail.messages] : [];
    list.sort((a, b) => {
      const ta = new Date(a.created_at).getTime();
      const tb = new Date(b.created_at).getTime();
      if (ta !== tb) return ta - tb;
      return a.sequence - b.sequence;
    });
    return list;
  }, [detail?.messages]);

  const saveMessageLabel = async (messageId: string) => {
    if (!integrationId) return;
    const key = `msg:${messageId}`;
    setLabelSaveErrorByKey((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    const d = messageDrafts[messageId] ?? emptyDraft();
    setSavingMessage(messageId);
    try {
      const trimmedComment = d.comment.trim();
      const trimmedRef = d.reference_answer.trim();
      const refPayload = trimmedRef === '' ? null : trimmedRef;
      const body: ChatViewerLabelUpsertBody = {
        comment: trimmedComment,
        reference_answer: refPayload,
        ground_truth: refPayload,
      };
      if (d.reaction !== null) body.reaction = d.reaction;
      await vuser.chatViewerPutMessageLabel(integrationId, messageId, body);
      const r = await vuser.chatViewerGetMessageResolvedLabel(integrationId, messageId);
      setMessageResolved((prev) => ({
        ...prev,
        [messageId]: pickPayload<ChatViewerResolvedLabel>(r) ?? null,
      }));
      await reloadMessageLabel(messageId, { clearedReference: trimmedRef === '' });
      flashLabelSaved(key);
    } catch (e: unknown) {
      setLabelSaveErrorByKey((prev) => ({ ...prev, [key]: humanLabelSaveErrorMessage(e) }));
    } finally {
      setSavingMessage(null);
    }
  };

  const reloadMessageLabel = async (messageId: string, opts?: { clearedReference?: boolean }) => {
    if (!integrationId) return;
    try {
      const r = await vuser.chatViewerGetMessageLabel(integrationId, messageId);
      const payload = pickPayload<{ label?: ChatViewerHumanLabelFields | null }>(r) ?? {};
      let next = draftFromHuman(payload?.label);
      if (opts?.clearedReference) {
        next = { ...next, reference_answer: '' };
      }
      setMessageDrafts((prev) => ({ ...prev, [messageId]: next }));
    } catch {
      /* ignore */
    }
  };

  const saveStepLabel = async (stepId: string) => {
    if (!integrationId) return;
    const key = `step:${stepId}`;
    setLabelSaveErrorByKey((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    const d = stepDrafts[stepId] ?? emptyDraft();
    setSavingStep(stepId);
    try {
      const trimmedComment = d.comment.trim();
      const trimmedRef = d.reference_answer.trim();
      const refPayload = trimmedRef === '' ? null : trimmedRef;
      const body: ChatViewerLabelUpsertBody = {
        comment: trimmedComment,
        reference_answer: refPayload,
        ground_truth: refPayload,
      };
      if (d.reaction !== null) body.reaction = d.reaction;
      await vuser.chatViewerPutStepLabel(integrationId, stepId, body);
      const r = await vuser.chatViewerGetStepResolvedLabel(integrationId, stepId);
      setStepResolved((prev) => ({ ...prev, [stepId]: pickPayload<ChatViewerResolvedLabel>(r) ?? null }));
      await reloadStepLabel(stepId, { clearedReference: trimmedRef === '' });
      flashLabelSaved(key);
    } catch (e: unknown) {
      setLabelSaveErrorByKey((prev) => ({ ...prev, [key]: humanLabelSaveErrorMessage(e) }));
    } finally {
      setSavingStep(null);
    }
  };

  const reloadStepLabel = async (stepId: string, opts?: { clearedReference?: boolean }) => {
    if (!integrationId) return;
    try {
      const r = await vuser.chatViewerGetStepLabel(integrationId, stepId);
      const payload = pickPayload<{ label?: ChatViewerHumanLabelFields | null }>(r) ?? {};
      let next = draftFromHuman(payload?.label);
      if (opts?.clearedReference) {
        next = { ...next, reference_answer: '' };
      }
      setStepDrafts((prev) => ({ ...prev, [stepId]: next }));
    } catch {
      /* ignore */
    }
  };

  if (!integrationId) {
    return null;
  }

  return (
    <div id="connector-chat-viewer-panel">
    <div className="chat-viewer-root" id="connector-chat-viewer-root">
      <p className="chat-viewer-intro">
        <i className="fas fa-circle-info chat-viewer-intro__icon" aria-hidden />
        <span>
          Review dialog messages from the Virtual User service. Final and step-level evaluations come from{' '}
          <strong>resolved-label</strong> endpoints (human &gt; admin &gt; user for reaction; human &gt; admin for
          comment / reference). Use human label forms to upsert your review via Chat Viewer APIs.
        </span>
      </p>

      <div className="chat-viewer-toolbar" id="chat-viewer-toolbar">
        <label>
          Filter by virtual user
          <select
            value={filterVuId}
            onChange={(e) => setFilterVuId(e.target.value)}
            aria-label="Filter dialogs by virtual user"
          >
            <option value="">All virtual users</option>
            {virtualUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} ({u.type})
              </option>
            ))}
          </select>
        </label>
        <span className="chat-viewer-toolbar__spacer" />
        <button type="button" className="action-btn refresh-btn" onClick={() => void loadDialogs()} disabled={dialogsLoading}>
          <i className={`fas fa-sync-alt ${dialogsLoading ? 'fa-spin' : ''}`} aria-hidden />
          <span className="btn-text">{dialogsLoading ? 'Loading…' : 'Refresh dialogs'}</span>
        </button>
      </div>

      {dialogsError && (
        <div className="chat-viewer-error" role="alert">
          <i className="fas fa-exclamation-circle" aria-hidden />
          <span>{dialogsError}</span>
        </div>
      )}

      <div className="chat-viewer-layout" id="chat-viewer-layout">
        <div className="chat-viewer-dialog-list">
          <div className="chat-viewer-dialog-list__header">Dialogs ({dialogs.length})</div>
          {dialogs.length === 0 && !dialogsLoading ? (
            <div className="chat-viewer-empty">
              <i className="fas fa-inbox" aria-hidden />
              No dialogs for this filter.
            </div>
          ) : (
            dialogs.map((d) => (
              <button
                key={d.id}
                type="button"
                className={`chat-viewer-dialog-item ${selectedDialogId === d.id ? 'chat-viewer-dialog-item--active' : ''}`}
                aria-current={selectedDialogId === d.id}
                onClick={() => setSelectedDialogId(d.id)}
              >
                <span className="chat-viewer-dialog-item__title" title={d.title}>
                  {d.title || 'Untitled dialog'}
                </span>
                <span className="chat-viewer-dialog-item__meta">
                  <span
                    className={`chat-viewer-dialog-item__dot chat-viewer-dialog-item__dot--${dialogStatusClass(d.status)}`}
                    aria-hidden
                  />
                  {dialogStatusText(d.status)}
                  <span className="chat-viewer-dialog-item__date">
                    {new Date(d.created_at).toLocaleString()}
                  </span>
                </span>
              </button>
            ))
          )}
        </div>

        <div className="chat-viewer-thread">
          {!selectedDialogId ? (
            <div className="chat-viewer-empty">
              <i className="fas fa-comments" aria-hidden />
              Choose a dialog from the list.
            </div>
          ) : detailLoading ? (
            <div className="chat-viewer-empty">
              <div className="spinner spinner--small" />
              Loading dialog…
            </div>
          ) : detailError ? (
            <div className="chat-viewer-error" role="alert">
              <i className="fas fa-exclamation-circle" aria-hidden />
              <span>{detailError}</span>
            </div>
          ) : detail ? (
            <>
              <div className="chat-viewer-thread__header">
                <div className="chat-viewer-thread__heading">
                  <div className="chat-viewer-thread__title">{detail.title || 'Dialog'}</div>
                  <div className="chat-viewer-thread__id">{detail.id}</div>
                </div>
                <span className={`chat-viewer-status chat-viewer-status--${dialogStatusClass(detail.status)}`}>
                  {dialogStatusText(detail.status)}
                </span>
              </div>

              {sortedMessages.map((msg) => {
                if (isDialogReasoningRole(msg.role)) {
                  return (
                    <div key={msg.id} className="chat-viewer-msg chat-viewer-msg--reasoning">
                      <div className="chat-viewer-msg__role">
                        <i className="fas fa-brain" aria-hidden />
                        Reasoning
                      </div>
                      <div className="chat-viewer-msg__content">
                        <ChatMarkdownBody>{msg.content ?? ''}</ChatMarkdownBody>
                      </div>
                    </div>
                  );
                }
                if (msg.role !== 'user' && msg.role !== 'assistant') {
                  return (
                    <div key={msg.id} className="chat-viewer-msg chat-viewer-msg--system">
                      <div className="chat-viewer-msg__role">
                        <i className="fas fa-gear" aria-hidden />
                        {msg.role || 'Message'}
                      </div>
                      <div className="chat-viewer-msg__content">
                        <ChatMarkdownBody>{msg.content ?? ''}</ChatMarkdownBody>
                      </div>
                    </div>
                  );
                }
                return (
                <div
                  key={msg.id}
                  className={`chat-viewer-msg chat-viewer-msg--${msg.role === 'user' ? 'user' : 'assistant'}`}
                >
                  <div className="chat-viewer-msg__role">
                    <i className={`fas fa-${msg.role === 'user' ? 'user' : 'robot'}`} aria-hidden />
                    {msg.role === 'user' ? 'User' : 'Assistant'}
                  </div>
                  <div className="chat-viewer-msg__content">
                    <ChatMarkdownBody>{msg.content ?? ''}</ChatMarkdownBody>
                  </div>

                  {msg.role === 'assistant' && (
                    <>
                      <div className="chat-viewer-section-label">Resolved label</div>
                      <ResolvedBlock resolved={messageResolved[msg.id]} />

                      <HumanLabelEditor
                        title="Human label (message)"
                        draft={messageDrafts[msg.id] ?? emptyDraft()}
                        setDraft={(updater) =>
                          setMessageDrafts((prev) => {
                            const cur = prev[msg.id] ?? emptyDraft();
                            const next = typeof updater === 'function' ? updater(cur) : updater;
                            return { ...prev, [msg.id]: next };
                          })
                        }
                        saving={savingMessage === msg.id}
                        saveSuccess={labelSaveSuccessKeys[`msg:${msg.id}`] === true}
                        errorMessage={labelSaveErrorByKey[`msg:${msg.id}`] ?? null}
                        onSave={() => saveMessageLabel(msg.id)}
                        onReload={() => reloadMessageLabel(msg.id)}
                      />
                    </>
                  )}

                  {Array.isArray(msg.steps) && msg.steps.length > 0 && (
                    <div className="chat-viewer-steps">
                      <button
                        type="button"
                        className="chat-viewer-steps__toggle"
                        onClick={() => togglePipeline(msg.id, msg.steps)}
                      >
                        <i className={`fas fa-${expandedPipelineForMessage === msg.id ? 'chevron-up' : 'chevron-down'}`} />
                        Reasoning ({msg.steps.length})
                      </button>
                      {expandedPipelineForMessage === msg.id &&
                        msg.steps!.map((step, stepIndex) => {
                          const sid = String(step.id);
                          const agentName = getPipelineStepDisplayName(step, stepIndex);
                          return (
                            <div key={sid} className="chat-viewer-step">
                              <div className="chat-viewer-step__name">
                                <i className="fas fa-diagram-project" aria-hidden />
                                {agentName}
                              </div>
                              <div className="chat-viewer-step__body">
                                <ChatMarkdownBody>{getDialogStepBodyDisplay(step)}</ChatMarkdownBody>
                              </div>
                              {stepLoading[sid] ? (
                                <div className="chat-viewer-hint">Loading step metadata…</div>
                              ) : (
                                <>
                                  <div className="chat-viewer-section-label">Resolved (step)</div>
                                  <ResolvedBlock resolved={stepResolved[sid]} />
                                  {(stepReactions[sid] || []).length > 0 && (
                                    <div className="chat-viewer-virtual-react">
                                      <strong>Virtual reactions:</strong>
                                      {(stepReactions[sid] || []).map((rx, i) => (
                                        <span key={i}>
                                          <b>{rx.user_type || '?'}</b>
                                          {rx.type || '—'}
                                          {rx.content ? ` — ${rx.content}` : ''}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                  <HumanLabelEditor
                                    title="Human label (step)"
                                    draft={stepDrafts[sid] ?? emptyDraft()}
                                    setDraft={(updater) =>
                                      setStepDrafts((prev) => {
                                        const cur = prev[sid] ?? emptyDraft();
                                        const next = typeof updater === 'function' ? updater(cur) : updater;
                                        return { ...prev, [sid]: next };
                                      })
                                    }
                                    saving={savingStep === sid}
                                    saveSuccess={labelSaveSuccessKeys[`step:${sid}`] === true}
                                    errorMessage={labelSaveErrorByKey[`step:${sid}`] ?? null}
                                    onSave={() => saveStepLabel(sid)}
                                    onReload={() => reloadStepLabel(sid)}
                                  />
                                </>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  )}

                  {msg.role === 'assistant' &&
                    (!msg.steps || msg.steps.length === 0) &&
                    msg.intermediate_messages &&
                    msg.intermediate_messages.length > 0 && (
                      <div className="chat-viewer-legacy-note">
                        <em>
                          Legacy <code>intermediate_messages</code> on the assistant message. Prefer{' '}
                          <code>steps[]</code> with <code>reasoning_messages</code> or separate reasoning rows.
                        </em>
                      </div>
                    )}
                </div>
                );
              })}
            </>
          ) : null}
        </div>
      </div>
    </div>
    </div>
  );
};

export default ConnectorChatViewerPanel;
